import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import DhcManagementPanel from "../components/business/DhcManagementPanel";
import { getSiteProfile, isNaviHost } from "../lib/site-profile";
import {
  Building2,
  CalendarDays,
  Heart,
  Loader2,
  ShieldCheck,
  Waves,
} from "lucide-react";

const siteProfile = getSiteProfile();

function formatOccasionLabel(value) {
  if (!value) return "General";
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function AdminPage() {
  const standaloneNavi = isNaviHost();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastTarget, setBroadcastTarget] = useState("global");
  const [broadcastStatus, setBroadcastStatus] = useState("");
  const [savingBroadcast, setSavingBroadcast] = useState(false);
  const [overview, setOverview] = useState({
    siteMode: standaloneNavi ? "navi" : "bajan-premier",
    reservations: [],
    businessTiers: [],
    romanticOccasions: [],
    stays: [],
  });

  useEffect(() => {
    const loadOverview = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`${API}/admin/navi/overview`, {
          withCredentials: true,
        });
        setOverview(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Unable to load Navi admin data.");
      } finally {
        setLoading(false);
      }
    };

    loadOverview();
  }, []);

  const reservationCount = overview.reservations.length;
  const businessTierCount = overview.businessTiers.length;
  const romanticCount = overview.romanticOccasions.length;
  const staysCount = overview.stays.length;

  const priorityReservations = useMemo(
    () =>
      overview.reservations.filter((reservation) => {
        const category = String(
          reservation.category ||
            reservation.occasion_type ||
            reservation.reservation_type ||
            ""
        ).toLowerCase();
        return category.includes("romantic") || category.includes("stay");
      }),
    [overview.reservations]
  );

  const submitBroadcast = async () => {
    if (!broadcastMessage.trim()) return;
    setSavingBroadcast(true);
    setBroadcastStatus("");
    try {
      const response = await axios.post(
        `${API}/ticker/broadcast`,
        {
          message: broadcastMessage.trim(),
          target_domain: broadcastTarget,
        },
        { withCredentials: true }
      );
      setBroadcastStatus(`Broadcast active for ${response.data.target_domain}.`);
      setBroadcastMessage("");
      window.dispatchEvent(new Event("navi-live-status-refresh"));
    } catch (err) {
      setBroadcastStatus(err.response?.data?.detail || "Unable to publish the HQ broadcast.");
    } finally {
      setSavingBroadcast(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b bg-white/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-orange-500 flex items-center justify-center">
              <Waves className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-serif font-bold text-lg text-slate-900">{siteProfile.brandName}</p>
              <p className="text-xs text-slate-500">Admin Console</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-100 text-emerald-700 border-0">
              {standaloneNavi ? "Navi Admin Active" : "Shared Platform Admin"}
            </Badge>
            <Link to="/dashboard">
              <Button variant="outline" className="rounded-full">
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mr-3" />
            Loading Navi admin data...
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 p-8 text-center">
            <h1 className="text-2xl font-serif font-bold text-slate-900 mb-3">Admin access issue</h1>
            <p className="text-amber-700 mb-4">{error}</p>
            <p className="text-sm text-slate-600">
              This route expects the live Mansion data network to expose the
              <code> reservations</code> and <code>business_tiers</code> collections to the backend environment.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="rounded-3xl bg-white p-6 shadow-sm border border-slate-100">
                <CalendarDays className="w-8 h-8 text-cyan-600 mb-3" />
                <p className="text-3xl font-bold text-slate-900">{reservationCount}</p>
                <p className="text-slate-500">Reservations</p>
              </div>
              <div className="rounded-3xl bg-white p-6 shadow-sm border border-slate-100">
                <Building2 className="w-8 h-8 text-violet-600 mb-3" />
                <p className="text-3xl font-bold text-slate-900">{businessTierCount}</p>
                <p className="text-slate-500">Business tiers</p>
              </div>
              <div className="rounded-3xl bg-white p-6 shadow-sm border border-slate-100">
                <Heart className="w-8 h-8 text-rose-500 mb-3" />
                <p className="text-3xl font-bold text-slate-900">{romanticCount}</p>
                <p className="text-slate-500">Romantic occasions</p>
              </div>
              <div className="rounded-3xl bg-white p-6 shadow-sm border border-slate-100">
                <ShieldCheck className="w-8 h-8 text-emerald-600 mb-3" />
                <p className="text-3xl font-bold text-slate-900">{staysCount}</p>
                <p className="text-slate-500">Stay-ready businesses</p>
              </div>
            </section>

            <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="rounded-3xl bg-white p-6 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-serif font-bold text-slate-900">Priority reservations</h2>
                  <Badge className="bg-slate-100 text-slate-700 border-0">
                    {priorityReservations.length} active
                  </Badge>
                </div>
                <div className="space-y-3">
                  {priorityReservations.length === 0 ? (
                    <p className="text-slate-500 text-sm">
                      No romantic or stay-focused reservations were returned by the backend.
                    </p>
                  ) : (
                    priorityReservations.slice(0, 8).map((reservation) => (
                      <div
                        key={reservation.id}
                        className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold text-slate-900">
                              {reservation.guest_name ||
                                reservation.customer_name ||
                                reservation.name ||
                                "Reservation"}
                            </p>
                            <p className="text-sm text-slate-500">
                              {reservation.business_name ||
                                reservation.location ||
                                reservation.property_name ||
                                "Managed in Navi admin"}
                            </p>
                          </div>
                          <Badge className="bg-rose-100 text-rose-700 border-0">
                            {formatOccasionLabel(
                              reservation.category ||
                                reservation.occasion_type ||
                                reservation.reservation_type
                            )}
                          </Badge>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-3xl bg-white p-6 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-serif font-bold text-slate-900">Business tiers</h2>
                  <Badge className="bg-cyan-100 text-cyan-700 border-0">
                    Mansion network synced
                  </Badge>
                </div>
                <div className="space-y-3">
                  {overview.businessTiers.length === 0 ? (
                    <p className="text-slate-500 text-sm">
                      No business tier rows were returned from the live Mansion network.
                    </p>
                  ) : (
                    overview.businessTiers.slice(0, 8).map((row) => (
                      <div
                        key={row.id}
                        className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 flex items-center justify-between gap-3"
                      >
                        <div>
                          <p className="font-semibold text-slate-900">
                            {row.business_name || row.name || row.business_id || "Business"}
                          </p>
                          <p className="text-sm text-slate-500">
                            {row.category || row.segment || row.site_origin || "Navi business"}
                          </p>
                        </div>
                        <Badge className="bg-violet-100 text-violet-700 border-0">
                          {formatOccasionLabel(row.tier || row.business_tier || "basic")}
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </section>

            <DhcManagementPanel
              broadcastTarget={broadcastTarget}
              setBroadcastTarget={setBroadcastTarget}
              broadcastMessage={broadcastMessage}
              setBroadcastMessage={setBroadcastMessage}
              submitBroadcast={submitBroadcast}
              savingBroadcast={savingBroadcast}
              broadcastStatus={broadcastStatus}
            />

            <section className="rounded-3xl bg-white p-6 shadow-sm border border-slate-100">
              <h2 className="text-xl font-serif font-bold text-slate-900 mb-4">
                Local Bajan stays and romantic management
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
                  <p className="text-sm uppercase tracking-wide text-emerald-700 font-semibold mb-2">
                    Stays
                  </p>
                  <ul className="space-y-2 text-sm text-slate-700">
                    {overview.stays.length === 0 ? (
                      <li>No stay-ready businesses returned.</li>
                    ) : (
                      overview.stays.slice(0, 6).map((stay) => (
                        <li key={stay.id || stay.business_id || stay.name}>
                          {stay.business_name || stay.name || stay.business_id}
                        </li>
                      ))
                    )}
                  </ul>
                </div>
                <div className="rounded-2xl bg-rose-50 border border-rose-100 p-4">
                  <p className="text-sm uppercase tracking-wide text-rose-700 font-semibold mb-2">
                    Romantic occasions
                  </p>
                  <ul className="space-y-2 text-sm text-slate-700">
                    {overview.romanticOccasions.length === 0 ? (
                      <li>No romantic occasions returned.</li>
                    ) : (
                      overview.romanticOccasions.slice(0, 6).map((occasion) => (
                        <li key={occasion.id || occasion.reservation_id || occasion.name}>
                          {occasion.guest_name ||
                            occasion.customer_name ||
                            occasion.name ||
                            "Romantic reservation"}
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
